import { Section, VStack } from 'tio-ui/components/layout';
import { dropTask } from 'ember-concurrency';
import { PhPaperPlaneTilt, PhSealCheck, PhWarningCircle } from 'ember-phosphor-icons';
import { tracked } from '@glimmer/tracking';
import Button from 'tio-ui/components/button';
import Callout from 'tio-ui/components/callout';
import Modal from 'tio-ui/components/modal';
import PhoneVerificationFlow from 'tio-employee/components/app/phone-verification/flow';
import PageHero from 'tio-ui/components/page-hero';
import Card from 'tio-ui/components/card';
import Heading from 'tio-ui/components/heading';
import { or } from 'tio-ui/utilities';
import { reloadRecord } from 'tio-data/requests/save-record';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import Component from '@glimmer/component';
import formatPhone from 'tio-common/helpers/format-phone';
import Svg from 'tio-employee/components/svg';
import type { CompanySettingExtension as CompanySettingModel } from 'tio-data/data/resources/company-setting.ext';
import type FeaturesService from 'tio-common/services/features';
import type PartnerService from 'tio-employee/services/partner';
import type SessionContextService from 'tio-employee/services/session-context';
import type ZendeskService from 'tio-employee/services/zendesk';
import type { Store as StoreService } from '@warp-drive/core';

export interface S {
  Args: {
    model: CompanySettingModel;
  };
}

export default class ContactUs extends Component<S> {
  @service declare zendesk: ZendeskService;
  @service declare partner: PartnerService;
  @service declare sessionContext: SessionContextService;
  @service declare features: FeaturesService;
  @service declare store: StoreService;

  /** Which step the verification modal is open at, or undefined when it is closed. */
  @tracked verifyingFrom?: 'own' | 'replacement';

  get support() {
    return this.partner.supportModule;
  }

  get isZendeskEnabled() {
    return this.zendesk.isEnabledForPartner;
  }

  get supportEmail() {
    if (!this.support || this.support?.tioEmail.isEnabled) {
      return 'support@tuition.io';
    } else if (this.support?.partnerEmail) {
      return this.support.partnerEmail;
    } else {
      return false;
    }
  }

  get tioPhoneEnabled() {
    return !this.support || this.support?.tioPhone.isEnabled;
  }

  get partnerPhoneNumber() {
    if (this.support?.partnerPhone) {
      return this.support.partnerPhone;
    } else {
      return false;
    }
  }

  get showPhoneCapture() {
    return this.features.can('phoneCapture.surfaces.supportPage');
  }

  /** No point offering to improve a call the company does not take. */
  get hasPhoneSupport() {
    return !!(this.partnerPhoneNumber || this.tioPhoneEnabled);
  }

  get showCaptureBlock() {
    return this.showPhoneCapture && this.hasPhoneSupport;
  }

  get captureNumber() {
    return this.sessionContext.currentEmployee?.activeMobilePhoneNumber?.number ?? '';
  }

  /** formatPhone has no international support, so only a +1 number is prettified. */
  get isNanpNumber() {
    return this.captureNumber.startsWith('+1');
  }

  get captureState() {
    return this.sessionContext.currentEmployee?.phoneVerificationState ?? 'none';
  }

  /** The number to hand the flow: their own when verifying it, none when replacing it. */
  get numberForFlow() {
    return this.verifyingFrom === 'own' ? this.captureNumber || undefined : undefined;
  }

  verifyOwnNumber = () => {
    this.verifyingFrom = 'own';
  };

  verifyReplacementNumber = () => {
    this.verifyingFrom = 'replacement';
  };

  /**
   * Refreshes on every exit, not just on success: the flow stores the number before it
   * sends a code, so skipping or closing after replacing one has still changed the record.
   */
  closeVerification = () => {
    this.verifyingFrom = undefined;
    this.refreshEmployee.perform();
  };

  onVerified = this.closeVerification;

  /**
   * Reloads just the phone numbers rather than the whole session: `sessionContext.load`
   * logs the member out on any failure, and a callout refresh must not end a valid session.
   */
  refreshEmployee = dropTask(async () => {
    const employee = this.sessionContext.currentEmployee;
    if (!employee) {
      return;
    }

    await reloadRecord(this.store, employee, { include: 'phone-numbers' });
  });

  onClickChat = () => {
    this.zendesk.openWebChatWindow();
  };

  <template>
    <VStack class="gap-8">
      <PageHero
        @title={{t "contact_us.default"}}
        @gradient="brand-soft"
        class="tio-elevation-card-raised"
      />
      {{#if this.showCaptureBlock}}
        {{#if (eq this.captureState "verified")}}
          <Callout @intent="success" @align="center" data-test-support-capture-verified>
            <:icon><PhSealCheck @size={{24}} @weight="fill" /></:icon>
            <:default>
              <p>{{t
                  "contact_us.verified_body"
                  number=(if this.isNanpNumber (formatPhone this.captureNumber) this.captureNumber)
                }}</p>
            </:default>
          </Callout>
        {{else}}
          <Callout @intent="warning" data-test-support-capture-prompt>
            <:icon><PhWarningCircle @size={{24}} @weight="fill" /></:icon>
            <:default>
              <h3 class="tio-callout__title" data-test-support-capture-header>
                {{#if (eq this.captureState "unverified")}}
                  {{t
                    "contact_us.verify_prompt_header"
                    number=(if
                      this.isNanpNumber (formatPhone this.captureNumber) this.captureNumber
                    )
                  }}
                {{else}}
                  {{t "contact_us.add_number_header"}}
                {{/if}}
              </h3>
              {{! One key for both states: what verifying buys the caller is the same
                  promise, and it turns on a product ruling that may yet change. }}
              <p>{{t "contact_us.verify_prompt_body"}}</p>
            </:default>
            <:action>
              <div class="tio-col-flush items-start gap-2">
                {{#if (eq this.captureState "unverified")}}
                  <Button
                    @intent="primary"
                    {{on "click" this.verifyOwnNumber}}
                    data-test-support-verify-number
                  >
                    <PhPaperPlaneTilt @size={{16}} class="inline mr-1" />
                    {{t "contact_us.verify_cta"}}
                  </Button>
                  <Button
                    @appearance="link"
                    @size="sm"
                    {{on "click" this.verifyReplacementNumber}}
                    data-test-support-use-different-number
                  >{{t "contact_us.use_different_number"}}</Button>
                {{else}}
                  <Button
                    @intent="primary"
                    {{on "click" this.verifyReplacementNumber}}
                    data-test-support-add-number
                  >
                    <PhPaperPlaneTilt @size={{16}} class="inline mr-1" />
                    {{t "contact_us.add_number_cta"}}
                  </Button>
                {{/if}}
              </div>
            </:action>
          </Callout>
        {{/if}}
      {{/if}}

      <Section>
        <:header>
          {{t "contact_us.ways_to_reach_us"}}
        </:header>
        <:body>
          <div class="app-card-grid app-card-grid--medium">
            {{#if this.supportEmail}}
              <Card
                @radius="card"
                @elevation="card"
                @padding="md"
                class="tio-stack-center text-center"
              >
                <Svg @name="email" width="45" height="45" />
                <Heading @level={{3}} @role="panel-title" class="mt-2">{{t
                    "contact_us.email_us"
                  }}</Heading>
                <a href="mailto:{{this.supportEmail}}" class="app-text-link tio-text-sm-medium">
                  {{this.supportEmail}}
                </a>
              </Card>
            {{/if}}
            {{#if (or this.partnerPhoneNumber this.tioPhoneEnabled)}}
              <Card
                @radius="card"
                @elevation="card"
                @padding="md"
                class="tio-stack-center text-center"
              >
                <Svg @name="call" width="45" height="45" />
                <Heading @level={{3}} @role="panel-title" class="mt-2">{{t
                    "contact_us.call_us"
                  }}</Heading>
                <div class="tio-text-sm-normal tio-fg-muted mt-1">
                  {{#if this.tioPhoneEnabled}}
                    <p>{{t "contact_us.call_us_times" htmlSafe=true}}</p>
                    <p>{{t "contact_us.tio_support_phone"}}</p>
                  {{else if this.partnerPhoneNumber}}
                    <p>{{formatPhone this.partnerPhoneNumber}}</p>
                  {{/if}}
                </div>
              </Card>
            {{/if}}
            {{#if this.isZendeskEnabled}}
              <Card
                @radius="card"
                @elevation="card"
                @padding="md"
                class="tio-stack-center text-center"
              >
                <Svg @name="headset_mic" width="45" height="45" />
                <Heading @level={{3}} @role="panel-title" class="mt-2">{{t
                    "contact_us.chat_with_us"
                  }}</Heading>
                <button
                  type="button"
                  class="app-text-link tio-text-sm-medium"
                  {{on "click" this.onClickChat}}
                >{{t "contact_us.live_chat_now"}}</button>
              </Card>
            {{/if}}
          </div>
        </:body>
      </Section>
    </VStack>

    {{#if this.verifyingFrom}}
      <Modal
        @isOpen={{true}}
        @onClose={{this.closeVerification}}
        @size="md"
        data-test-support-phone-verification
      >
        <:body>
          <PhoneVerificationFlow
            @surface="supportPage"
            @phoneNumber={{this.numberForFlow}}
            @onVerified={{this.onVerified}}
            @onDeferred={{this.closeVerification}}
            @onCancel={{this.closeVerification}}
          />
        </:body>
      </Modal>
    {{/if}}
  </template>
}
